import type { CalcAst, CalcParseError, Span } from './ast'
import { lexTableauCalc, type Token } from './tableauLexer'

export interface ParseResult {
  ast: CalcAst | null
  errors: CalcParseError[]
  tokens: Token[]
}

function spanFrom(a: Span, b: Span): Span {
  return { start: Math.min(a.start, b.start), end: Math.max(a.end, b.end) }
}

export function parseTableauCalc(input: string): ParseResult {
  const { tokens } = lexTableauCalc(input)
  const p = new Parser(tokens)
  const ast = p.parseExpression()
  p.expect('eof')
  return { ast, errors: p.errors, tokens }
}

class Parser {
  private i = 0
  public readonly errors: CalcParseError[] = []
  private readonly tokens: Token[]

  constructor(tokens: Token[]) {
    // TS config uses `erasableSyntaxOnly`; avoid parameter properties.
    this.tokens = tokens
  }

  private peek(offset = 0): Token {
    return this.tokens[Math.min(this.tokens.length - 1, this.i + offset)]
  }

  private next(): Token {
    const t = this.peek(0)
    this.i = Math.min(this.tokens.length - 1, this.i + 1)
    return t
  }

  private atEof(): boolean {
    return this.peek().kind === 'eof'
  }

  private error(message: string, span: Span) {
    this.errors.push({ message, span })
  }

  private match(kind: Token['kind'], textLower?: string): Token | null {
    const t = this.peek()
    if (t.kind !== kind) return null
    if (textLower !== undefined) {
      if (String(t.text || '').toLowerCase() !== textLower) return null
    }
    this.next()
    return t
  }

  private matchKeyword(word: string): Token | null {
    return this.match('keyword', word.toLowerCase())
  }

  public expect(kind: Token['kind'], textLower?: string): Token {
    const t = this.peek()
    if (t.kind === kind && (textLower === undefined || String(t.text || '').toLowerCase() === textLower)) {
      return this.next()
    }
    this.error(
      textLower
        ? `Expected ${kind} (${textLower}), found ${t.kind}${t.text ? ` (${t.text})` : ''}`
        : `Expected ${kind}, found ${t.kind}${t.text ? ` (${t.text})` : ''}`,
      t.span,
    )
    // Basic recovery: don’t consume EOF; otherwise consume one token.
    if (t.kind !== 'eof') return this.next()
    return t
  }

  // -------- Grammar entry --------
  public parseExpression(): CalcAst | null {
    return this.parseOr()
  }

  // or-expr := and-expr (OR and-expr)*
  private parseOr(): CalcAst | null {
    let left = this.parseAnd()
    while (this.matchKeyword('or')) {
      const opTok = this.tokens[this.i - 1]
      const right = this.parseAnd()
      if (!left || !right) return left || right
      left = { kind: 'binary', op: 'or', left, right, span: spanFrom(left.span, right.span) }
      ;(left as any).span = spanFrom(opTok.span, (left as any).span)
    }
    return left
  }

  // and-expr := not-expr (AND not-expr)*
  private parseAnd(): CalcAst | null {
    let left = this.parseNot()
    while (this.matchKeyword('and')) {
      const right = this.parseNot()
      if (!left || !right) return left || right
      left = { kind: 'binary', op: 'and', left, right, span: spanFrom(left.span, right.span) }
    }
    return left
  }

  // not-expr := (NOT)* compare-expr
  private parseNot(): CalcAst | null {
    if (this.matchKeyword('not')) {
      const opTok = this.tokens[this.i - 1]
      const expr = this.parseNot()
      if (!expr) return null
      return { kind: 'unary', op: 'not', expr, span: spanFrom(opTok.span, expr.span) }
    }
    return this.parseCompare()
  }

  // compare-expr := add-expr ( ( = | != | <> | < | <= | > | >= | IN ) add-expr )?
  private parseCompare(): CalcAst | null {
    let left = this.parseAdd()
    if (!left) return null

    const opTok =
      this.peek().kind === 'op'
        ? (['=', '!=', '<>', '<', '<=', '>', '>='].includes(this.peek().text) ? this.next() : null)
        : null

    if (!opTok && this.matchKeyword('in')) {
      const right = this.parseAdd()
      if (!right) return left
      return { kind: 'binary', op: 'in', left, right, span: spanFrom(left.span, right.span) }
    }

    if (opTok) {
      const op = opTok.text as any
      const right = this.parseAdd()
      if (!right) return left
      return { kind: 'binary', op, left, right, span: spanFrom(left.span, right.span) }
    }

    return left
  }

  // add-expr := mul-expr (('+'|'-') mul-expr)*
  private parseAdd(): CalcAst | null {
    let left = this.parseMul()
    while (true) {
      const op = this.peek().kind === 'op' && (this.peek().text === '+' || this.peek().text === '-') ? this.next() : null
      if (!op) break
      const right = this.parseMul()
      if (!left || !right) return left || right
      left = { kind: 'binary', op: op.text as any, left, right, span: spanFrom(left.span, right.span) }
    }
    return left
  }

  // mul-expr := unary-expr (('*'|'/') unary-expr)*
  private parseMul(): CalcAst | null {
    let left = this.parseUnary()
    while (true) {
      const op = this.peek().kind === 'op' && (this.peek().text === '*' || this.peek().text === '/') ? this.next() : null
      if (!op) break
      const right = this.parseUnary()
      if (!left || !right) return left || right
      left = { kind: 'binary', op: op.text as any, left, right, span: spanFrom(left.span, right.span) }
    }
    return left
  }

  // pow-expr := primary ('^' pow-expr)?
  private parsePow(): CalcAst | null {
    let left = this.parsePrimary()
    if (!left) return null
    if (this.peek().kind === 'op' && this.peek().text === '^') {
      const caret = this.next()
      let right: CalcAst | null
      if (this.peek().kind === 'op' && (this.peek().text === '+' || this.peek().text === '-')) {
        const sign = this.next()
        const rhs = this.parsePow()
        if (!rhs) return left
        right = {
          kind: 'unary',
          op: sign.text as any,
          expr: rhs,
          span: spanFrom(sign.span, rhs.span),
        }
      } else {
        right = this.parsePow()
      }
      if (!right) return left
      return {
        kind: 'binary',
        op: '^',
        left,
        right,
        span: spanFrom(caret.span, spanFrom(left.span, right.span)),
      }
    }
    return left
  }

  // unary-expr := ('+'|'-') unary-expr | pow-expr
  private parseUnary(): CalcAst | null {
    const t = this.peek()
    if (t.kind === 'op' && (t.text === '+' || t.text === '-')) {
      const opTok = this.next()
      const expr = this.parseUnary()
      if (!expr) return null
      return { kind: 'unary', op: opTok.text as any, expr, span: spanFrom(opTok.span, expr.span) }
    }
    return this.parsePow()
  }

  // primary := literal | field_ref | func_call | '(' expr ')' | if_expr | case_expr | lod_expr
  private parsePrimary(): CalcAst | null {
    const t = this.peek()

    // LOD expression: {FIXED|INCLUDE|EXCLUDE ... : ...}
    if (t.kind === 'lbrace') {
      return this.parseLod()
    }

    if (t.kind === 'keyword') {
      if (t.text === 'if') return this.parseIf()
      if (t.text === 'case') return this.parseCase()
      if (t.text === 'true' || t.text === 'false') {
        const tok = this.next()
        return { kind: 'literal', literal_kind: 'bool', value: tok.text === 'true', span: tok.span }
      }
      if (t.text === 'null') {
        const tok = this.next()
        return { kind: 'literal', literal_kind: 'null', value: null, span: tok.span }
      }
    }

    if (t.kind === 'number') {
      const tok = this.next()
      const n = Number(tok.text)
      if (!Number.isFinite(n)) {
        this.error(`Invalid number literal: ${tok.text}`, tok.span)
        return { kind: 'literal', literal_kind: 'number', value: 0, span: tok.span }
      }
      return { kind: 'literal', literal_kind: 'number', value: n, span: tok.span }
    }

    if (t.kind === 'string') {
      const tok = this.next()
      return { kind: 'literal', literal_kind: 'string', value: tok.text, span: tok.span }
    }

    if (t.kind === 'bracket_ident') {
      return this.parseFieldRef()
    }

    if (t.kind === 'identifier') {
      // Could be function call: IDENT '('
      const identTok = this.next()
      if (this.match('lparen')) {
        const args: CalcAst[] = []
        if (!this.match('rparen')) {
          while (!this.atEof()) {
            const expr = this.parseExpression()
            if (expr) args.push(expr)
            if (this.match('comma')) continue
            this.expect('rparen')
            break
          }
        }
        return {
          kind: 'call',
          name: identTok.text,
          args,
          span: spanFrom(identTok.span, this.tokens[this.i - 1]?.span ?? identTok.span),
        }
      }

      // Bare identifier is allowed but uncommon in Tableau; treat as field_ref for now.
      return {
        kind: 'field_ref',
        source: null,
        field: identTok.text,
        span: identTok.span,
      }
    }

    if (this.match('lparen')) {
      const l = this.tokens[this.i - 1]
      const expr = this.parseExpression()
      const r = this.expect('rparen')
      return expr ? { ...expr, span: spanFrom(l.span, r.span) } : null
    }

    this.error(`Unexpected token: ${t.kind}${t.text ? ` (${t.text})` : ''}`, t.span)
    if (t.kind !== 'eof') this.next()
    return null
  }

  private parseFieldRef(): CalcAst | null {
    const first = this.expect('bracket_ident')
    const baseName = String(first.text ?? '')

    // Optional qualified ref: [Source].[Field]
    const dot = this.match('dot')
    if (dot) {
      const second = this.expect('bracket_ident')
      const fieldName = String(second.text ?? '')
      return {
        kind: 'field_ref',
        source: baseName,
        field: fieldName,
        span: spanFrom(first.span, second.span),
      }
    }

    return {
      kind: 'field_ref',
      source: null,
      field: baseName,
      span: first.span,
    }
  }

  private parseIf(): CalcAst | null {
    const ifTok = this.expect('keyword', 'if')
    const branches: Array<{ when: CalcAst; then: CalcAst }> = []

    const when0 = this.parseExpression()
    this.expect('keyword', 'then')
    const then0 = this.parseExpression()
    if (when0 && then0) branches.push({ when: when0, then: then0 })

    while (this.matchKeyword('elseif')) {
      const w = this.parseExpression()
      this.expect('keyword', 'then')
      const t = this.parseExpression()
      if (w && t) branches.push({ when: w, then: t })
    }

    let elseExpr: CalcAst | null = null
    if (this.matchKeyword('else')) {
      elseExpr = this.parseExpression()
    }

    const endTok = this.expect('keyword', 'end')

    return {
      kind: 'if',
      branches,
      else_expr: elseExpr ?? null,
      span: spanFrom(ifTok.span, endTok.span),
    }
  }

  private parseCase(): CalcAst | null {
    const caseTok = this.expect('keyword', 'case')

    // CASE <base>? WHEN ... THEN ...
    // If next is WHEN, base is omitted.
    let base: CalcAst | null = null
    if (!(this.peek().kind === 'keyword' && this.peek().text === 'when')) {
      base = this.parseExpression()
    }

    const whens: Array<{ when: CalcAst; then: CalcAst }> = []
    while (this.matchKeyword('when')) {
      const w = this.parseExpression()
      this.expect('keyword', 'then')
      const t = this.parseExpression()
      if (w && t) whens.push({ when: w, then: t })
    }

    let elseExpr: CalcAst | null = null
    if (this.matchKeyword('else')) {
      elseExpr = this.parseExpression()
    }

    const endTok = this.expect('keyword', 'end')
    return {
      kind: 'case',
      base: base ?? null,
      whens,
      else_expr: elseExpr ?? null,
      span: spanFrom(caseTok.span, endTok.span),
    }
  }

  // lod_expr := '{' (FIXED | INCLUDE | EXCLUDE) dimension_list ':' expression '}'
  //           | '{' expression '}'   // table-scoped LOD (e.g. { AVG([Sales]) })
  // dimension_list := (field_ref (',' field_ref)*)?
  private parseLod(): CalcAst | null {
    const lbraceTok = this.expect('lbrace')
    const startSpan = lbraceTok.span

    const first = this.peek()
    const lodType = first.kind === 'keyword' ? String(first.text || '').toLowerCase() : ''

    // Table-scoped LOD: { <expression> }
    // Tableau uses this form for table-level aggregation without an explicit FIXED/INCLUDE/EXCLUDE keyword.
    if (lodType !== 'fixed' && lodType !== 'include' && lodType !== 'exclude') {
      const expression = this.parseExpression()
      if (!expression) {
        this.error('Expected expression in LOD braces', this.peek().span)
        return null
      }
      const rbraceTok = this.expect('rbrace')
      return {
        kind: 'lod',
        lod_type: 'fixed',
        dimensions: [],
        expression,
        span: spanFrom(startSpan, rbraceTok.span),
      }
    }

    // Parse explicit LOD type: FIXED, INCLUDE, or EXCLUDE
    const lodTypeTok = this.next()
    if (lodType !== 'fixed' && lodType !== 'include' && lodType !== 'exclude') {
      this.error(`Expected FIXED, INCLUDE, or EXCLUDE, found: ${lodTypeTok.text}`, lodTypeTok.span)
      return null
    }

    // Parse dimension list (comma-separated field references)
    const dimensions: CalcAst[] = []
    while (!this.atEof()) {
      const peek = this.peek()
      if (peek.kind === 'colon') {
        break // End of dimension list
      }
      let dim: CalcAst | null = null
      if (peek.kind === 'bracket_ident') {
        dim = this.parseFieldRef()
      } else {
        // Allow non-field expressions in LOD dimensions (e.g., DATETRUNC(...))
        dim = this.parseExpression()
      }
      if (dim) {
        dimensions.push(dim)
      } else {
        break
      }
      if (this.match('comma')) {
        continue
      }
      if (this.peek().kind === 'colon') {
        break
      }
    }

    // Expect colon separator
    this.expect('colon')

    // Parse the aggregated expression
    const expression = this.parseExpression()
    if (!expression) {
      this.error('Expected expression after colon in LOD expression', this.peek().span)
      return null
    }

    // Expect closing brace
    const rbraceTok = this.expect('rbrace')

    return {
      kind: 'lod',
      lod_type: lodType as 'fixed' | 'include' | 'exclude',
      dimensions,
      expression,
      span: spanFrom(startSpan, rbraceTok.span),
    }
  }
}

